import { useEffect, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useStore } from '../state/store';
import { maxPanelWidth, PANEL_MAX, PANEL_MIN, PANEL_STEP, viewportWidth } from '../lib/panel';

/**
 * The grab handle on the side panel's left edge.
 *
 * `role="separator"` with `aria-valuenow`, not a `<div>` with a mousedown: this
 * is the window-splitter pattern, so it is reachable by Tab and movable by
 * arrow key — a shop on a laptop trackpad, or anyone who cannot drag an 8-px
 * target, still gets to widen the panel. Left widens (the handle moves left);
 * Home/End jump to the bounds.
 *
 * The drag uses pointer capture, so the pointer may leave the 8-px strip — over
 * the canvas, over the panel's own controls, outside the window — without the
 * drag being dropped, and there are no document-level listeners to leak.
 */
export function PanelResizer() {
  const width = useStore((s) => s.viewer.panelWidth);
  const setPanelWidth = useStore((s) => s.setPanelWidth);
  const [dragging, setDragging] = useState(false);
  /** Local, because it is a fact about the WINDOW rather than about the app:
   *  `aria-valuemax` has to follow a resize even when the width itself does not
   *  change (the store setter is a no-op then, so nothing else would re-render). */
  const [max, setMax] = useState(() => maxPanelWidth(viewportWidth()));

  useEffect(() => {
    const onResize = () => {
      setMax(maxPanelWidth(viewportWidth()));
      // Re-clamped, not merely re-measured: a width saved on a wide monitor
      // would otherwise leave a 300-px viewer on a laptop. The setter itself
      // clamps and returns early when nothing moved.
      setPanelWidth(useStore.getState().viewer.panelWidth);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setPanelWidth]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // The drag must not also select the panel's text or start a native drag.
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    // The panel is flush with the window's right edge, so its width is simply
    // whatever is left of the viewport to the right of the pointer.
    setPanelWidth(window.innerWidth - e.clientX);
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = useStore.getState().viewer.panelWidth;
    if (e.key === 'ArrowLeft') setPanelWidth(current + PANEL_STEP);
    else if (e.key === 'ArrowRight') setPanelWidth(current - PANEL_STEP);
    else if (e.key === 'Home') setPanelWidth(PANEL_MIN);
    else if (e.key === 'End') setPanelWidth(PANEL_MAX);
    else return;
    // Only for the keys actually handled: Tab, and the browser's own shortcuts,
    // still belong to the browser.
    e.preventDefault();
  };

  return (
    <div
      className="panel-resizer"
      role="separator" aria-orientation="vertical" aria-label="Resize panel"
      aria-valuemin={PANEL_MIN} aria-valuemax={max} aria-valuenow={width}
      tabIndex={0}
      data-dragging={dragging ? 'true' : undefined}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      onPointerUp={endDrag} onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    />
  );
}
